<template>
  <div>
    <SectionSeparatorWrapper :separator="false" headerText="Eligibility" headerRequiredFieldsText="indicates required field">
      <EligibilityForm
        v-if="isActive || !disableEligibilityFields"
        :refData="informationReferenceData"
        :eligibilityType="changeCoverData.step1.eligibility"
        :currentEligibility="currentEligibility"
        :formData="eligibility"
        :coverStartDate="new Date(changeCoverData.step1.startDate)"
        :contactIdOrMemberNumber="memberNumber"
        :partnerInfoChanged="partnerInfoChanged"
        :isChangeCover="true"
        @responseUpdated="updateEligibilityResponse"
      />
    </SectionSeparatorWrapper>
  </div>
</template>

<script>
import isEqual from 'lodash.isequal';
import EligibilityForm from '@general/js/templates/JoinNow/JoinNowSteps/FormSection/EligibilityForm.vue';
import { NeedsAnalysisEligibility } from '@general/js/enums';
import { isEmptyString } from '@/utils';
import { SectionSeparatorWrapper } from '@/components';
import { AcceptableThisEligibilityType } from '@/enums';
import CaptureInformationMixins from '../CaptureInformationMixins';
import ChangeCoverMixins from '../ChangeCoverMixins';

export default {
  name: 'Eligibility',

  mixins: [CaptureInformationMixins, ChangeCoverMixins],

  components: {
    EligibilityForm,
    SectionSeparatorWrapper,
  },

  props: {
    informationReferenceData: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      eligibility: {
        serviceConnection: '',
        serviceConnectionName: '',
        declaration: null,
        declarationDate: null,
        declarationExpiryDate: null,
        subFormData: {},
      },
      subFormIsInvalid: true,
      isFormInvalid: true,
      isActive: false,
    };
  },

  beforeMount() {
    // Prefill data when no data stored in vuex
    this.prefillData();
  },

  activated() {
    this.isActive = true;
    this.prefillData();
  },

  deactivated() {
    this.isActive = false;
  },

  validations: {
    isFormInvalid: {
      isValid(bool) {
        return bool === false;
      },
    },
  },

  watch: {
    eligibility: {
      handler(newVal, oldVal) {
        if (this.isActive) {
          const eligibility = { ...this.eligibility, ...this.eligibility.subFormData };
          delete eligibility.subFormData;
          this.$nextTick(() => {
            this.$emit('formUpdated', {
              response: eligibility,
              isInvalid: this.isFormInvalid,
            });
          });

          // Any data changed will reset declaration to false
          if (
            !this.disableEligibilityFields &&
            newVal.declaration === oldVal.declaration &&
            !isEqual(this.formatEligibilityFormData(newVal), this.formatEligibilityFormData(oldVal))
          ) {
            this.eligibility.declaration = false;
          }
        }
      },
      deep: true,
    },
  },

  computed: {
    // Only no eligibility type changed and no partnerInfoChanged, the form will be prefilled with CRM data
    loadCRMEligibilityData() {
      return !this.partnerInfoChanged && this.currentEligibility === this.changeCoverData.step1.eligibility;
    },

    disableEligibilityFields() {
      return this.currentEligibility === this.changeCoverData.step1.eligibility && !this.partnerInfoChanged;
    },
  },

  methods: {
    updateEligibilityResponse(data, invalid) {
      this.isFormInvalid = invalid;
      this.eligibility = { ...this.eligibility, ...data };
    },

    setUpData() {
      let subFormTemplate = {};

      const veteranInfo = { ...this.eligibilityInformationStep.veteranInfo };

      switch (this.changeCoverData.step1.eligibility) {
        case AcceptableThisEligibilityType.SERVING:
          subFormTemplate = {
            enlistmentDate: this.eligibilityInformationStep.enlistmentDate,
            sercatStatus: this.eligibilityInformationStep.sercatStatus,
            sercatFileNames: [],
            veteranInfo,
          };
          break;
        case AcceptableThisEligibilityType.EX_SERVING:
          subFormTemplate = {
            enlistmentDate: this.eligibilityInformationStep.enlistmentDate,
            dischargeDate: this.eligibilityInformationStep.dischargeDate,
            veteranInfo,
          };
          break;
        case AcceptableThisEligibilityType.RELATED:
          subFormTemplate = {
            relationName: this.eligibilityInformationStep.relationName,
            relationship: this.eligibilityInformationStep.relationship,
            connectionName: this.eligibilityInformationStep.connectionName,
            connectionType: this.eligibilityInformationStep.connectionType,
            veteranInfo,
          };
          break;
        default:
          subFormTemplate = {
            relationName: this.eligibilityInformationStep.relationName,
            connectionName: this.eligibilityInformationStep.connectionName,
            connectionType: this.eligibilityInformationStep.connectionType,
          };
          break;
      }

      return subFormTemplate;
    },

    // Format form data to compare the changes
    formatEligibilityFormData(data, isDeclarationDateIncluded = false, isDeclarationIncluded = true) {
      // To make sure that veteranInfo in the root level
      const newFormData = {
        ...data,
        ...data?.subFormData,
      };
      delete newFormData.subFormData;
      if (!isDeclarationDateIncluded) {
        delete newFormData.declarationDate;
      }
      if (!isDeclarationIncluded) {
        delete newFormData.declaration;
      }
      if (this.changeCoverData.step1.eligibility === NeedsAnalysisEligibility.RELATED) {
        const initialVeteranInfo = { ...newFormData?.veteranInfo };
        if (!Object.keys(initialVeteranInfo).length) {
          delete newFormData.veteranInfo;
          return newFormData;
        }
        if (isEmptyString(initialVeteranInfo?.rank)) {
          delete initialVeteranInfo.rank;
          return {
            ...newFormData,
            veteranInfo: {
              ...initialVeteranInfo,
            },
          };
        }
      }
      return newFormData;
    },

    // Prefill form data
    prefillData() {
      let formData;
      if (isEqual(this.formatEligibilityFormData(this.initialData.eligibility, true, false), this.formatEligibilityFormData(this.eligibilityInformationStep, true, false))) {
        // Empty value stored in vuex, then use initial data to prefill
        formData = { ...this.getEligibilityInitialData };
      } else if (
        !isEqual(this.formatEligibilityFormData(this.getEligibilityInitialData, true, false), this.formatEligibilityFormData(this.eligibilityInformationStep, true, false))
      ) {
        // Changed value stored in vuex, then use ne changed data to prefill
        formData = { ...this.eligibilityInformationStep };
      } else {
        // Initial value stored in vuex, then use initial data to prefill
        formData = { ...this.getEligibilityInitialData };
      }

      this.updateEligibilityStep(formData);

      this.eligibility.serviceConnection = formData.serviceConnection;
      this.eligibility.serviceConnectionName = formData.serviceConnectionName;
      this.eligibility.subFormData = this.setUpData();
      this.eligibility.declarationExpiryDate = formData.declarationExpiryDate;
      this.eligibility.declarationDate = formData.declarationDate;
      this.eligibility.declaration = formData.declaration;
    },
    isEmptyString,
  },
};
</script>

<style></style>
